import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

export interface DuplicatePair {
  contact_a: number;
  contact_b: number;
  name_a: string;
  name_b: string;
  reason: string;
  confidence: number;
}

/**
 * Duplicate pairs for the active workspace.
 *
 * Duplicates are NOT a CRM module, there is no sidebar entry and no
 * `/duplicates` page. Cleaning up records is not a job anyone sits down to do;
 * it is something you notice while looking at a contact. So detection runs
 * once per workspace here and the result is filtered per record, which also
 * means opening ten contacts costs one query rather than ten.
 */
export const useDuplicates = () => {
  const workspaceId = getActiveWorkspaceId();

  return useQuery<DuplicatePair[]>({
    queryKey: ["duplicates", workspaceId],
    enabled: Boolean(workspaceId),
    // Detection scans the whole contact table; it does not need to be fresh to
    // the second, and a stale-for-a-minute warning is still a correct warning.
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "find_duplicate_contacts",
        { p_workspace_id: workspaceId, p_limit: 200 },
      );
      if (error) throw new Error(error.message);
      return (data ?? []) as DuplicatePair[];
    },
  });
};

/** The pairs involving one contact, with that contact's side normalised. */
export const duplicatesForContact = (
  pairs: DuplicatePair[] | undefined,
  contactId: number,
): DuplicatePair[] =>
  (pairs ?? []).filter(
    (pair) => pair.contact_a === contactId || pair.contact_b === contactId,
  );

/** The id of the OTHER contact in a pair. */
export const otherContactId = (pair: DuplicatePair, contactId: number) =>
  pair.contact_a === contactId ? pair.contact_b : pair.contact_a;

export const otherContactName = (pair: DuplicatePair, contactId: number) =>
  pair.contact_a === contactId ? pair.name_b : pair.name_a;

/** Every contact id that appears in any pair, used to flag rows in the list. */
export const duplicateIdSet = (
  pairs: DuplicatePair[] | undefined,
): Set<number> => {
  const ids = new Set<number>();
  (pairs ?? []).forEach((pair) => {
    ids.add(pair.contact_a);
    ids.add(pair.contact_b);
  });
  return ids;
};
